'use client';

import styled, { css } from 'styled-components';
import { Spinner, Text } from '@lmn/design-system';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useMetaReview } from '@/app/components/hotel-detail/hooks/useMetaReview';
import { decorateRatingScore, getColor, getInterval } from '@/app/components/hotel-detail/trust-you/trustyou.helper';
import TrustYouScore from '@/app/components/hotel-detail/trust-you/TrustYouScore';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { formatNumber } from '@/utils/numberUtils.ts';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { useHotelRanking } from './useHotelRanking';

export type TrustYouRatingsPopoverProps = {
  hotelId: number | undefined;
  language: string;
  onViewMore?: () => void;
};

// #region styles

const Container = styled.div`
  @media (max-width: 767px) {
    min-height: 80px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;

const ScoreInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ReviewCount = styled(Text).attrs({ variant: 'Body' })`
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
`;

const CategoryList = styled.ul`
  list-style: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media (max-width: 767px) {
    margin-bottom: 12px;
    gap: 8px;
  }
`;

const CategoryRow = styled.li`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ProgressBar = styled.div<{ $score: number; $color: string; $isMobile: boolean }>`
  background-color: ${(p) => p.theme.kallisto.color.background.static.backgroundDisabled};
  border-radius: 8px;
  height: 8px;
  position: relative;
  width: ${({ $isMobile }) => ($isMobile ? '125px' : '100px')};
  max-width: 100%;
  margin-left: auto;

  &::after {
    background-color: ${({ $color }) => $color};
    border-radius: 3px;
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: ${({ $score }) => $score}%;
  }
`;

const Footer = styled.div``;

const ViewMoreButton = styled.button`
  padding: 0;
  cursor: pointer;
  background: none;
  border: none;
  color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryHover};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  justify-content: center;
  min-height: 40px;

  @media (max-width: 767px) {
    min-height: 80px;
  }
`;

// #endregion styles

function LoadingState() {
  const { t } = useTranslation();

  return (
    <LoadingContainer>
      <Spinner size={20} variant="accent" />
      <Text variant="BodySmall" color="textPrimary">
        {t('vacation.oss.components.searchfilters.main.loading')}
      </Text>
    </LoadingContainer>
  );
}

function NoDataFallback() {
  const { t } = useTranslation();

  return (
    <Container style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Text variant="Body" color="textPrimary">
        {t('vacation.oss.cards.reviews.not_available')}
      </Text>
    </Container>
  );
}

export default function TrustYouRatingsPopover({ hotelId, language, onViewMore }: TrustYouRatingsPopoverProps) {
  const { localization } = usePageWrapperData();
  const { isMobile } = useCommonClient();
  const { locale } = localization;
  const { t } = useTranslation();
  const { trustYouHotelId, isLoading: isLoadingTrustYouId, isError: isErrorTrustYouId } = useHotelRanking(hotelId);
  const { metaReview, isLoading, isError } = useMetaReview(trustYouHotelId, language);

  if (isLoadingTrustYouId || isLoading) {
    return <LoadingState />;
  }

  if (isErrorTrustYouId || !trustYouHotelId || isError || !metaReview) {
    return <NoDataFallback />;
  }

  const { score, categories, reviewCount } = metaReview;
  const ratingInterval = score ? getInterval(score) : undefined;
  const reviewCountFormatted = formatNumber({ locale, number: reviewCount });

  return (
    <Container>
      <Header>
        {score ? (
          <TrustYouScore
            score={score}
            css={`
              font-size: 24px;
              width: 56px;
              height: 56px;
              padding: 6px;
              display: flex;
              flex-shrink: 0;
              justify-content: center;
              align-items: center;
            `}
          />
        ) : null}
        <ScoreInfo>
          {ratingInterval && <Text variant="Title4">{t(ratingInterval.translationKey)}</Text>}
          <ReviewCount>
            {reviewCountFormatted} {t('vacation.oss.components.searchresults.tour_tile.reviews')}
          </ReviewCount>
        </ScoreInfo>
      </Header>

      <CategoryList>
        {categories.map((category) => (
          <CategoryRow key={category.id}>
            <CategoryHeader>
              <Text variant={isMobile ? 'Body' : 'BodySmall'}>{category.name}</Text>
              <ProgressBar $score={category.score} $color={getColor(category.score)} $isMobile={isMobile} />
              <Text
                variant={isMobile ? 'Body' : 'BodySmall'}
                css={css`
                  margin-left: 8px;
                  flex-shrink: 0;
                `}
              >
                {decorateRatingScore(category.score)}
              </Text>
            </CategoryHeader>
          </CategoryRow>
        ))}
      </CategoryList>

      {onViewMore && (
        <Footer>
          <ViewMoreButton type="button" onClick={onViewMore}>
            {t('vacation.select.details.review.viewmore')}
          </ViewMoreButton>
        </Footer>
      )}
    </Container>
  );
}
